// สำหรับแสดง Format ต่าง ๆ ของ Leave Reqeust List
import React, { useCallback, useMemo } from "react";

import { Dropdown, Form, Icon, Popup } from "semantic-ui-react";

import moment, { Moment } from "moment";

// Serializer
import LeaveTypeSerializer from "issara-sdk/types/LeaveTypeSerializer_apps_HRM";

// Common
import TimeComboBoxStatic from "react-lib/apps/common/TimeComboBoxStatic";

import { ShiftTimeMapType, ShiftTimeSaveType } from "./CardEmployeeLeaveRequest"; // todo --- ย้ายไปตั้งใน sequence/...
import { checkIsInvalidShiftTime } from "./TableEmployeeLeaveRequest";

import { calculateNetWorkingHours } from "./sequence/SetupWorkSchedule";

// Utils
import {
  checkLeaveTimeOutsideShift,
  formatTimeHideSeconds,
  isTimeWithinShift,
} from "./HRMInterface";

import { formatDateDashToSlash } from "react-lib/utils/dateUtils";

// Based on LeaveRequestList API
export type LeaveRequestListType = {
  id: number;
  created_by_full_name: string;
  employee_code: string;
  description: string;
  documents: { id: number; file: string }[];
  end_date: string; // format "2025-06-11", "yyyy-mm-dd"
  leave_type: number;
  reject_remark: string;
  remaining_leave_days: number;
  start_date: string; // format "2025-06-11", "yyyy-mm-dd"
  status: StatusCode;
  total_leave_days: number;
  total_remaining_leave_days: string;
  work_shift_detail: WorkShiftDetailType;
};

export type WorkShiftDetailType = {
  work_shift: WorkShiftType[] | null;
  work_shift_list: WorkShiftType[] | null;
};

export type WorkShiftType = Partial<{
  id: number;
  ad_year: number;
  approval_status: "" | "A" | "P" | "U" | "X"; // *
  day: number;
  end_break_time: string | null;
  end_time: string | null;
  is_break_time: boolean;
  leave_end_time: string | null;
  leave_start_time: string | null;
  month: number;
  shift_type: "" | "D" | "N"; //*
  start_break_time: string | null;
  start_time: string | null;
  template: number | null;
}>;

export type StatusCode = "A" | "P" | "U" | "X"; // "P" = Pending, "A" = Approve, "U" = Unapprove, "X" = Reject

// Component Type
type RenderLeaveTypeProps = {
  item: LeaveRequestListType;
  leaveTypeOptions: LeaveTypeSerializer[];
};

type RenderStatusProps = {
  item: LeaveRequestListType;
  remark?: string;
  status: StatusCode;
};

type RenderDocumentProps = {
  item: LeaveRequestListType;
  leaveTypeList: LeaveTypeSerializer[];
};

type RenderShiftTimeProps = {
  date: Moment;
  shiftTime: ShiftTimeSaveType[];
  status: "A" | "P" | "U" | "X";
  workShiftItem: WorkShiftDetailType;
  onChange: (values: ShiftTimeSaveType[]) => void;
};

type RenderLeaveTimeProps = {
  date: Moment;
  shiftTime?: ShiftTimeSaveType[];
  status: "A" | "P" | "U" | "X";
  workShiftItem: WorkShiftDetailType;
  onChange: (values: ShiftTimeSaveType[]) => void;
};

type RenderTotalHourProps = {
  date: Moment;
  shiftTime: ShiftTimeSaveType[];
  status: "A" | "P" | "U" | "X";
  totalLeaveDays: number;
  workShiftItem: WorkShiftDetailType;
};

type RenderLeaveDateProps = {
  workShiftList?: WorkShiftType[];
};

type RenderEmployeeProps = Partial<{
  fullName: string;
  employeeCode: string;
}>;

/**================================================================================================
 **                                         RENDERING
 *================================================================================================**/

export const RenderEmployee = (props: RenderEmployeeProps) => {
  const { fullName, employeeCode } = props;

  if (fullName) {
    const formattedEmpCode = employeeCode ? `(${employeeCode})` : "";

    return <label>{`${fullName} ${formattedEmpCode}`}</label>;
  }

  return null;
};

// Leave Type (id) -> Leave Type (name) with Description
export const RenderLeaveType = (props: RenderLeaveTypeProps) => {
  const { item, leaveTypeOptions } = props;

  const leaveTypeText = leaveTypeOptions.find((opt) => opt.id === item.leave_type)?.name || "-";

  return (
    <div
      id={`RenderLeaveType-Div-LeaveType-${item.id}`}
      style={{
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      <span>{leaveTypeText}</span>
      {!!item.description && (
        <Popup content={item.description} trigger={<Icon color="blue" name="sticky note" />} />
      )}
    </div>
  );
};

// Status -> Status Text with Status Color
export const RenderStatus = (props: RenderStatusProps) => {
  const { remark, status, item } = props;

  const statusMap: Partial<Record<StatusCode, { color: string; text: string }>> = {
    A: { color: "#21ba45", text: "Approved" },
    P: { color: "#f2c037", text: "Pending" },
    U: { color: "#767676", text: "Unapproved" },
    X: { color: "#db2828", text: "Rejected" },
  };

  const current = statusMap[status];

  if (!current) {
    return <label id={`RenderStatus-Label-Status-${item.id}`}>{status}</label>;
  }

  const label = <span id={`RenderStatus-Span-Status-${item.id}`} style={{ color: current.color }}>{current.text}</span>;

  if (status === "X" && remark) {
    return (
      <div
        style={{
          alignItems: "baseline",
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        {label}
        <Popup content={remark} trigger={<Icon color="red" name="sticky note" />} />
      </div>
    );
  }

  return label;
};

// แสดงรายการ Attach File
export const RenderDocument = (props: RenderDocumentProps) => {
  const { item, leaveTypeList } = props;

  const matchedLeaveType = leaveTypeList.find((type) => type.id === item.leave_type);

  const isRequired = matchedLeaveType?.attachment_required;
  const hasDocs = item.documents.length > 0;

  if (isRequired && !hasDocs) {
    return (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <label style={{ color: "red" }}>**Document Required**</label>
      </div>
    );
  }

  if (hasDocs) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          width: "120px",
          wordBreak: "break-all",
        }}
      >
        {item.documents.map((doc: any, index: number) => {
          const fileName = extractFileFromUrl(doc.file);

          return (
            <a
              key={doc.id || index}
              style={{ color: "#4183c4", textDecoration: "underline" }}
              href={doc.file}
              rel="noopener noreferrer"
              target="_blank"
            >
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <Icon color="blue" name="file outline" />
                {fileName}
              </div>
            </a>
          );
        })}
      </div>
    );
  }

  return null;
};

export type RenderDisplayShiftTimeProps = {
  date: Moment;
  workShiftList?: WorkShiftType[];
};

export const RenderDisplayShiftTime = (props: RenderDisplayShiftTimeProps) => {
  const { date, workShiftList } = props;

  const getOptions = useCallback((data: WorkShiftType) => {
    const label = `${formatTimeHideSeconds(data.start_time)} - ${formatTimeHideSeconds(
      data.end_time
    )}`;

    return [
      {
        key: data.id,
        text: label,
        value: data.id,
      },
    ];
  }, []);

  const matchedShifts = sortWorkShiftList(workShiftList ?? []).filter((s) =>
    moment(`${s.ad_year}-${s.month}-${s.day}`).isSame(date, "day")
  );

  if (matchedShifts.length === 0) {
    return <div style={{ minWidth: "8.75rem" }}>No Shift Time</div>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", width: "100%" }}>
      {matchedShifts.map((s) => {
        const breakTime =
          s.is_break_time && s.start_break_time && s.end_break_time
            ? `${formatTimeHideSeconds(s.start_break_time)} - ${formatTimeHideSeconds(
                s.end_break_time
              )}`
            : null;
        const options = getOptions(s);

        const { height } = calculateShiftDisplay(workShiftList ?? [], -1, s);

        return (
          <Form key={s.id} style={{ height }} size="small">
            <Dropdown
              style={{ minWidth: "8.75rem" }}
              value={s.id}
              disabled
              fluid
              selection
              options={options}
            />
            {!!breakTime && (
              <div style={{ color: "gray", fontSize: 12, marginTop: 2 }}>Break: {breakTime}</div>
            )}
          </Form>
        );
      })}
    </div>
  );
};

export const RenderShiftTime = (props: RenderShiftTimeProps) => {
  const { date, shiftTime, status, workShiftItem, onChange } = props;

  const { isReadOnly, sourceShiftList } = initializeShiftData({
    date,
    shiftTime,
    status,
    workShiftItem,
  });

  const selectedIds = useMemo(() => shiftTime.map((s) => s.id), [date, shiftTime]);

  const handleShiftTimeChange = (currentSelectedIds: number[]) => {
    const values = getShiftTimeValues({
      date,
      selectedIds: currentSelectedIds,
      shiftTime,
      sourceShiftList,
    });

    const hasMissingData = values.some(
      (v) => !shiftTime.some((s) => s.id === v.id && s.date === v.date)
    );

    const hasObsoleteData = shiftTime.some(
      (s) => s.date === date.format("YYYY-MM-DD") && !currentSelectedIds.includes(s.id)
    );

    if ((hasMissingData || hasObsoleteData) && !isReadOnly) {
      onChange(values);
    }
  };

  const getShiftById = (id: number): WorkShiftType => {
    if (id === 0) {
      return { id: 0, end_time: "", is_break_time: false, start_time: "" };
    }

    return (
      sourceShiftList.find((s) => s.id === id) ?? {
        id: 0,
        end_time: "",
        is_break_time: false,
        start_time: "",
      }
    );
  };

  const formatOption = (shift: WorkShiftType): { key: number; text: string; value: number } => ({
    key: shift.id ?? 0,
    text:
      shift.id === 0
        ? "No Shift Time"
        : `${formatTimeHideSeconds(shift.start_time)} - ${formatTimeHideSeconds(shift.end_time)}`,
    value: shift.id ?? 0,
  });

  const allOptions = [
    ...sourceShiftList.map((element) => formatOption(element)),
    { key: 0, text: "No Shift Time", value: 0 },
  ];

  const handleAddDropdown = () => {
    const remaining = sourceShiftList
      .map((s) => Number(s.id))
      .filter((id) => !selectedIds.includes(id));

    if (remaining.length > 0) {
      handleShiftTimeChange([...selectedIds, remaining[0]]);
    }
  };

  const handleRemoveDropdown = (index: number) => {
    const updatedSelectedIds = selectedIds.filter((unused, currentIndex) => currentIndex !== index);

    const values = getShiftTimeValues({
      date,
      selectedIds: updatedSelectedIds,
      shiftTime,
      sourceShiftList,
    });

    onChange(values);
  };

  const handleSelectChange = (index: number, value: number) => {
    const newIds = [...selectedIds];

    newIds[index] = value;

    handleShiftTimeChange(newIds);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      {selectedIds.map((id, index) => {
        const selected = getShiftById(id);

        const { hasIcon, height } = calculateShiftDisplay(sourceShiftList, index, selected);

        return (
          <Form key={index} style={{ height, position: "relative" }} size="small">
            {!isReadOnly && hasIcon && (
              <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: "6px" }}>
                {index === 0 && sourceShiftList.length > 1 && (
                  <Icon color="blue" name="plus" link onClick={handleAddDropdown} />
                )}
                {index > 0 && (
                  <Icon
                    color="red"
                    name="minus"
                    link
                    onClick={() => {
                      handleRemoveDropdown(index);
                    }}
                  />
                )}
              </div>
            )}

            <Dropdown
              style={{ minWidth: "8.75rem" }}
              disabled={isReadOnly}
              placeholder="Select Shift Time"
              value={selected.id ?? 0}
              fluid
              selection
              options={allOptions}
              onChange={(e, { value }) => {
                handleSelectChange(index, value as number);
              }}
            />

            {selected.is_break_time && !!selected.start_break_time && !!selected.end_break_time && (
              <label style={{ color: "gray", fontSize: "12px", marginTop: "4px" }}>
                Break: {formatTimeHideSeconds(selected.start_break_time)} -{" "}
                {formatTimeHideSeconds(selected.end_break_time)}
              </label>
            )}
          </Form>
        );
      })}
    </div>
  );
};

export type RenderDisplayLeaveTimeProps = {
  date: Moment;
  workShiftList?: WorkShiftType[];
};

export const RenderDisplayLeaveTime = (props: RenderDisplayLeaveTimeProps) => {
  const { date, workShiftList } = props;

  const shiftForDate = sortWorkShiftList(workShiftList ?? []).filter((s) =>
    moment(`${s.ad_year}-${s.month}-${s.day}`, "YYYY-M-D").isSame(date, "day")
  );

  if (shiftForDate.length === 0) {
    return (
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <TimeComboBoxStatic style={{ width: "85px" }} value={undefined} disabled />
        <label style={{ marginTop: "7px" }}>to</label>
        <TimeComboBoxStatic style={{ width: "85px" }} value={undefined} disabled />
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", width: "100%" }}>
      {shiftForDate.map((shift) => {
        const { height } = calculateShiftDisplay(workShiftList ?? [], -1, shift);

        return (
          <Form
            key={shift.id}
            style={{
              display: "flex",
              gap: "0.5rem",
              height,
              width: "100%",
            }}
            size="small"
          >
            <TimeComboBoxStatic
              style={{ width: "85px" }}
              value={shift.leave_start_time ? shift.leave_start_time.slice(0, 5) : undefined}
              disabled
            />
            <label style={{ marginTop: "7px" }}>to</label>
            <TimeComboBoxStatic
              style={{ width: "85px" }}
              value={shift.leave_end_time ? shift.leave_end_time.slice(0, 5) : undefined}
              disabled
            />
          </Form>
        );
      })}
    </div>
  );
};

export const RenderLeaveTime = (props: RenderLeaveTimeProps) => {
  const { date, shiftTime, status, workShiftItem, onChange } = props;

  const { isReadOnly, sourceShiftList } = initializeShiftData({
    date,
    shiftTime: shiftTime ?? [],
    status,
    workShiftItem,
  });

  const handleChange = (data: {
    field: "leave_end_time" | "leave_start_time";
    index: number;
    shift?: WorkShiftType;
    value: string;
  }) => {
    const { field, index, shift, value } = data;

    const updated = [...(shiftTime ?? [])];

    updated[index] = {
      ...updated[index],
      [field]: value,
    };

    const leaveStart = updated[index].leave_start_time;
    const leaveEnd = updated[index].leave_end_time;

    const { isEndOutside, isStartOutside, shiftEnd, shiftStart } = checkLeaveTimeOutsideShift(
      leaveStart,
      leaveEnd,
      shift
    );

    if (!isStartOutside && !isEndOutside && shiftStart) {
      // ตรวจสอบว่าช่วงเวลาลาทั้งหมดอยู่ในกะการทำงานหรือไม่
      const isLeaveWithinShift = isTimeWithinShift(shiftStart, shiftEnd, leaveStart, leaveEnd);

      // หากช่วงเวลาลาไม่อยู่ในกะ ให้ล้างค่า
      if (!isLeaveWithinShift) {
        updated[index].leave_end_time = "";
      }
    }

    onChange(updated.map((s) => ({ ...s, date: date.format("YYYY-MM-DD") })));
  };

  const getShiftById = (id: number) => sourceShiftList.find((shift) => shift.id === id);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", width: "100%" }}>
      {(shiftTime ?? []).map((item, index) => {
        const shift = getShiftById(item.id);

        const isDisabled = !shift?.start_time || item.id === 0;

        const startTimeValue =
          item.leave_start_time ||
          (isReadOnly ? formatTimeHideSeconds(shift?.leave_start_time) : "");
        const endTimeValue =
          item.leave_end_time || (isReadOnly ? formatTimeHideSeconds(shift?.leave_end_time) : "");

        const leaveTime = checkLeaveTimeOutsideShift(startTimeValue, endTimeValue, shift);

        const { hasIcon, height } = calculateShiftDisplay(sourceShiftList, index, shift);

        return (
          <Form
            key={index}
            style={{
              display: "flex",
              gap: "0.5rem",
              height,
              paddingTop: hasIcon ? 20 : 0,
              width: "100%",
            }}
            size="small"
          >
            <TimeComboBoxStatic
              style={{ width: "85px" }}
              disabled={isDisabled}
              inputStyle={leaveTime.isStartOutside ? { color: "red" } : {}}
              value={startTimeValue || undefined}
              onTextChange={(value: string) => {
                handleChange({ field: "leave_start_time", index, shift, value });
              }}
            />
            <label style={{ marginTop: "7px" }}>to</label>
            <TimeComboBoxStatic
              style={{ width: "85px" }}
              disabled={isDisabled}
              inputStyle={leaveTime.isEndOutside ? { color: "red" } : {}}
              value={endTimeValue || undefined}
              onTextChange={(value: string) => {
                handleChange({ field: "leave_end_time", index, shift, value });
              }}
            />
          </Form>
        );
      })}
    </div>
  );
};

// todo --- แสดงจำนวนชั่วโมง (depened on each day or total hour of leave request)
// export const RenderTotalHour = (props: RenderTotalHourProps) => {
//   const { totalLeaveDays, workShiftItem, shiftTime, status, date } = props;

//   const workShift = workShiftItem.work_shift || [];
//   const workShiftList = workShiftItem.work_shift_list || [];

//   const refHour = totalLeaveDays * 8; // * 1 Day = 8 Hrs

//   const effectiveShiftTime = (status === "A" || status === "X")
//   ? getShiftByDate(workShift, date).map((w) => ({
//       id: w.id,
//       leave_start_time: w.leave_start_time || "",
//       leave_end_time: w.leave_end_time || "",
//       date: moment(`${w.ad_year}-${w.month}-${w.day}`, "YYYY-M-D").format("YYYY-MM-DD"),
//     }))
//   : shiftTime;

//   const effectiveWorkShift = (status === "A" || status === "X")
//     ? workShift
//     : workShiftList;

//   const isAllNoShift = effectiveWorkShift.length === 0 || effectiveShiftTime?.every((item) => item.id === 0);

//   let actualTotalMinutes = 0;

//   if (!isAllNoShift) {
//     actualTotalMinutes = effectiveShiftTime.reduce((sum, item) => {
//       const shift = effectiveWorkShift.find((ws) => ws.id === item.id);

//       if (!shift || !item.leave_start_time || !item.leave_end_time) return sum;

//       const { hours, minutes } = calculateTotalWorkingTime({
//         startTime: item.leave_start_time,
//         endTime: item.leave_end_time,
//         isBreakTime: shift.is_break_time,
//         startBreakTime: shift.start_break_time,
//         endBreakTime: shift.end_break_time,
//       });

//       return sum + hours * 60 + minutes;
//     }, 0);
//   }

//   const actualHours = Math.floor(actualTotalMinutes / 60);
//   // const actualMinutes = actualTotalMinutes % 60; //

//   const isInvalid =
//     !isAllNoShift && (actualTotalMinutes > refHour * 60 || actualTotalMinutes < refHour * 60);

//   const display = isAllNoShift ? refHour : actualHours;

//   return <div style={{ color: isInvalid ? "red" : "black" }}>{display}</div>;
// };

export const RenderDisplayRequestedHour = ({
  date,
  shiftTime,
  status,
  totalLeaveDays,
  workShiftItem,
}: RenderTotalHourProps) => {
  const result = calculateLeaveHours({
    date,
    shiftTime,
    status,
    totalLeaveDays,
    workShiftItem,
  });
  const { requestedHourPerDay } = result;

  return <div>{requestedHourPerDay}</div>;
};

export const RenderDisplayQuotaLeaveHour = ({
  date,
  shiftTime,
  status,
  totalLeaveDays,
  workShiftItem,
}: RenderTotalHourProps) => {
  const result = calculateLeaveHours({
    date,
    shiftTime,
    status,
    totalLeaveDays,
    workShiftItem,
  });
  const { quotaHourPerDay } = result;

  return <div>{quotaHourPerDay}</div>;
};

export const RenderLeaveDate = (props: RenderLeaveDateProps) => {
  const dates = (props.workShiftList ?? [])
    .map((ws) => moment(`${ws.ad_year}-${ws.month}-${ws.day}`, "YYYY-MM-DD"))
    .sort((a, b) => a.diff(b));

  return (
    <>
      {dates.map((date, index) => (
        <div key={index}>{formatDateDashToSlash(date.format("DD-MM-YYYY"))}</div>
      ))}
    </>
  );
};

/**================================================================================================
 **                                         UTIL FUNCS
 *================================================================================================**/
export const getShiftTimeValuesMap = (leaveRequestList: LeaveRequestListType[]) => {
  const formattedLeaveRequests = formatLeaveRequestData(leaveRequestList, {});

  const requestEntries = formattedLeaveRequests.map((item) => {
    const allDateValues = item.leaveDates.flatMap((date) => {
      const shiftTime = item.shiftTimeByDate[date.format("YYYY-MM-DD")];

      const { initialSelectedIds, isReadOnly, sourceShiftList } = initializeShiftData({
        date,
        shiftTime,
        status: item.status,
        workShiftItem: item.work_shift_detail,
      });

      if (isReadOnly) {
        return [];
      }

      const values = getShiftTimeValues({
        date,
        selectedIds: initialSelectedIds,
        shiftTime,
        sourceShiftList,
      });

      return values.map((value) => ({ ...value, leave_end_time: "", leave_start_time: "" }));
    });

    return [item.id, allDateValues];
  });

  return Object.fromEntries(requestEntries);
};

const getLeaveDates = (item: LeaveRequestListType) => {
  const start = moment(item.start_date);
  const end = item.end_date ? moment(item.end_date) : start;

  const days: Moment[] = [];

  for (const d = moment(start); d.isSameOrBefore(end); d.add(1, "day")) {
    days.push(moment(d));
  }

  return days;
};

const sortWorkShiftList = (workShift: WorkShiftType[]) =>
  workShift
    .map((acc) => ({
      ...acc,
      end: moment(acc.end_time, "HH:mm"),
      start: moment(acc.start_time, "HH:mm"),
    }))
    .sort((a, b) => {
      const startDiff = a.start.valueOf() - b.start.valueOf();

      if (startDiff !== 0) {
        return startDiff;
      }

      return a.end.valueOf() - b.end.valueOf();
    });

export const formatLeaveRequestData = (
  leaveRequestList: LeaveRequestListType[],
  shiftTimeMap: ShiftTimeMapType
) =>
  leaveRequestList.map((item) => {
    const shiftTime = shiftTimeMap[item.id] || [];
    const workShift = [
      ...(item.work_shift_detail.work_shift_list ?? []),
      ...(item.work_shift_detail.work_shift ?? []),
    ];
    const sortedWorkShift = sortWorkShiftList(workShift);
    const isInvalidTime = checkIsInvalidShiftTime(shiftTime, sortedWorkShift);

    const leaveDates = getLeaveDates(item);

    // สร้าง shift time map สำหรับแต่ละวัน
    const shiftTimeByDate: Record<string, any[]> = {};

    for (const date of leaveDates) {
      shiftTimeByDate[date.format("YYYY-MM-DD")] = shiftTime.filter((s) =>
        moment(s.date).isSame(date, "day")
      );
    }

    // สร้าง shift time map จาก shiftTimeMap สำหรับแต่ละวัน
    const shiftTimeMapByDate: Record<string, any[]> = {};

    for (const date of leaveDates) {
      shiftTimeMapByDate[date.format("YYYY-MM-DD")] =
        shiftTimeMap[item.id]?.filter((s) => moment(s.date).isSame(date, "day")) || [];
    }

    return {
      ...item,
      isInvalidTime,
      leaveDates,
      shiftTime,
      shiftTimeByDate,
      shiftTimeMapByDate,
      workShift: sortedWorkShift,
    };
  });

export const initializeShiftData = (data: {
  date: Moment;
  shiftTime: ShiftTimeSaveType[];
  status: string;
  workShiftItem: WorkShiftDetailType;
}) => {
  const { date, shiftTime, status, workShiftItem } = data;

  const workShift = getShiftByDate(sortWorkShiftList(workShiftItem.work_shift ?? []), date);
  const workShiftList = getShiftByDate(
    sortWorkShiftList(workShiftItem.work_shift_list ?? []),
    date
  );
  const isReadOnly = status === "A" || status === "X";
  const sourceShiftList = isReadOnly ? workShift : workShiftList;

  const getInitialSelectedIds = (): number[] => {
    if (isReadOnly && shiftTime.length > 0) {
      return shiftTime.filter((s) => s.date === date.format("YYYY-MM-DD")).map((s) => s.id);
    }

    return sourceShiftList.length > 0 ? [sourceShiftList[0].id ?? 0] : [];
  };

  return {
    initialSelectedIds: getInitialSelectedIds(),
    isReadOnly,
    sourceShiftList,
    workShift,
    workShiftList,
  };
};

export const getShiftTimeValues = (data: {
  date: Moment;
  selectedIds: number[];
  shiftTime: ShiftTimeSaveType[];
  sourceShiftList: WorkShiftType[];
}): ShiftTimeSaveType[] => {
  const { date, selectedIds, shiftTime, sourceShiftList } = data;

  return selectedIds.map((id) => {
    const match = sourceShiftList.find((s) => s.id === id);
    const dateStr = match
      ? moment(`${match.ad_year}-${match.month}-${match.day}`, "YYYY-M-D").format("YYYY-MM-DD")
      : date.format("YYYY-MM-DD");
    const exist = shiftTime.find((s) => s.id === id && s.date === dateStr);

    return {
      id,
      date: dateStr,
      leave_end_time: exist?.leave_end_time || "",
      leave_start_time: exist?.leave_start_time || "",
    };
  });
};

const extractFileFromUrl = (url: string) => {
  try {
    const parseUrl = new URL(url);
    const pathName = parseUrl.pathname;
    const segments = pathName.split("/");
    const lastSegment = segments[segments.length - 1];

    if (!lastSegment || lastSegment === "{}") {
      return "unnamed_file";
    }

    return decodeURIComponent(lastSegment);
  } catch {
    return "invalid_url";
  }
};

export type CalculateLeaveHoursProps = {
  date?: Moment;
  shiftTime?: ShiftTimeSaveType[];
  status: string;
  totalLeaveDays: number;
  workShiftItem: WorkShiftDetailType;
};

const getShiftByDate = (shifts: WorkShiftType[], date: Moment) =>
  shifts.filter((s) => moment(`${s.ad_year}-${s.month}-${s.day}`, "YYYY-M-D").isSame(date, "day"));

const calculateShiftDisplay = (shifts: WorkShiftType[], index: number, shift?: WorkShiftType) => {
  const hasIcon = (index === 0 && shifts.length > 1) || index > 0;

  let height = 35;

  if (hasIcon) {
    height += 14;
  }

  height += shift?.is_break_time ? 20 : 5;

  return { hasIcon, height };
};

const calculateLeaveHours = (props: CalculateLeaveHoursProps) => {
  const { date, shiftTime, status, totalLeaveDays, workShiftItem } = props;

  const workShift = workShiftItem.work_shift ?? [];
  const workShiftList = workShiftItem.work_shift_list ?? [];

  const isApproved = ["A", "X"].includes(status);

  const allShiftTime = isApproved
    ? workShift
        .map((w) => ({
          id: w.id,
          date: moment(`${w.ad_year}-${w.month}-${w.day}`, "YYYY-M-D").format("YYYY-MM-DD"),
          leave_end_time: w.leave_end_time || "",
          leave_start_time: w.leave_start_time || "",
        }))
        .filter((w) => moment(w.date).isSame(date, "days"))
    : shiftTime ?? [];

  // เลือก work shift ที่เหมาะสม: ถ้าอนุมัติแล้วใช้ workShift, ถ้ายังไม่อนุมัติใช้ workShiftList
  const effectiveWorkShift = isApproved ? workShift : workShiftList;

  // ตรวจสอบว่าไม่มีกะการทำงานเลย หรือทุกรายการมี id = 0 (ไม่มีกะ)
  const isAllNoShift =
    effectiveWorkShift.length === 0 || allShiftTime.every((item) => item.id === 0);

  // กำหนดจำนวนชั่วโมงที่ขอลาต่อวัน: ครึ่งวัน = 4 ชั่วโมง, เต็มวัน = 8 ชั่วโมง
  const requestedHourPerDay = totalLeaveDays === 0.5 ? 4 : 8;

  let totalLeaveHours = null;

  // คำนวณชั่วโมงลาจริงเฉพาะเมื่อมีกะการทำงาน
  if (!isAllNoShift) {
    for (const item of allShiftTime) {
      // ข้ามรายการที่ไม่มีเวลาเริ่มต้นหรือสิ้นสุดการลา
      if (!item.leave_start_time || !item.leave_end_time) {
        continue;
      }

      // หากะการทำงานที่ตรงกับ id
      const shift = effectiveWorkShift.find((ws) => ws.id === item.id);

      // คำนวณชั่วโมงการลาสุทธิ (หักเวลาพักแล้ว)
      const leaveHours = calculateNetWorkingHours(
        item.leave_start_time,
        item.leave_end_time,
        shift
      );

      totalLeaveHours = (totalLeaveHours ?? 0) + leaveHours;
    }
  }

  return {
    quotaHourPerDay: totalLeaveHours === null ? 0 : totalLeaveHours - requestedHourPerDay,
    requestedHourPerDay,
  };
};
